import React, { useState } from 'react';

import Catalog from '~/Catalog';
import JustificationContainer from '~/components/atom/JustificationContainer';
import Dropdown, { type SelectedOption } from '~/components/molecule/Dropdown';
import type { DoContactAction_ReassignOfficeUser__Input } from '~/graphql/types';
import useErrorReporter from '~/hooks/useErrorReporter';
import useOfficeOptions from '~/hooks/useOfficeOptions';
import useTrackEvent from '~/hooks/useTrackEvent';
import useUserOptions from '~/hooks/useUserOptions';
import cleanedFilename from '~/util/cleanedFilename';
import ConfirmActionModal from '../../components/ConfirmActionModal';
import BasePopover from '../../components/BasePopover';
import type { ActionModalProps } from '../../constants';
import TEST_ID from './index.testid';

export type Props = {} & ActionModalProps;

const text = {
  title: 'Toewijzen aan vestiging & gebruiker',
  confirm: 'Bevestigen',
  officeLabel: Catalog.officeLabel,
  userLabel: Catalog.userLabel,
  officePlaceholder: Catalog.noOffice,
  userPlaceholder: Catalog.noUser,
};

const ReassignOfficeUser: React.FCC<Props> = ({
  lockContactsVariables,
  selectedContactAmount,
  onComplete,
  onCancel,
}) => {
  const [showModal, setShowModal] = useState(false);
  const hideModal = () => setShowModal(false);
  const trackEvent = useTrackEvent();
  const reporter = useErrorReporter();

  const [selectedOfficeId, setSelectedOfficeId] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const officeOptions = useOfficeOptions();
  const userOptionsFromOffice = useUserOptions({
    officeId: selectedOfficeId,
  });
  const userOptions = selectedOfficeId ? userOptionsFromOffice : [];

  const selectedOfficeIdx = officeOptions.findIndex(
    option => option.payload?.id === selectedOfficeId,
  );
  const selectedUserIdx = userOptions.findIndex(
    option => option.payload?.id === selectedUserId,
  );

  const canConfirm = selectedOfficeId != null && selectedUserId != null;

  return (
    <>
      {showModal && selectedOfficeId != null && selectedUserId != null ? (
        <ConfirmActionModal
          actionType="ReassignOfficeUser"
          lockContactsVariables={lockContactsVariables}
          onComplete={() => {
            trackEvent({
              eventName: 'contacts_reassigned_office_user',
              parameters: {
                contacts_amount: selectedContactAmount,
              },
            });
            hideModal();
            onComplete();
          }}
          onCancel={() => {
            hideModal();
            onCancel();
          }}
          selectedContactAmount={selectedContactAmount}
          actionInput={{
            ReassignOfficeUser: {
              officeId: selectedOfficeId,
              userId: selectedUserId,
            } as DoContactAction_ReassignOfficeUser__Input,
          }}
        />
      ) : (
        <BasePopover
          title={text.title}
          handleAction={confirm => {
            if (confirm) {
              setShowModal(true);
            } else {
              onCancel();
            }
          }}
          confirmText={text.confirm}
          disabled={!canConfirm}
        >
          <JustificationContainer
            direction="column"
            gap="base"
            dataTestId={TEST_ID.CONTAINER}
          >
            <Dropdown
              dataTestId={TEST_ID.OFFICE_DROPDOWN}
              label={text.officeLabel}
              placeholder={text.officePlaceholder}
              options={officeOptions}
              selectedOptionIdx={selectedOfficeIdx}
              required
              onChange={({ option }: SelectedOption) => {
                const nextOfficeId = option.payload?.id ?? null;
                setSelectedOfficeId(nextOfficeId);
                setSelectedUserId(null);
              }}
            />
            <Dropdown
              dataTestId={TEST_ID.USER_DROPDOWN}
              label={text.userLabel}
              placeholder={text.userPlaceholder}
              options={userOptions}
              selectedOptionIdx={selectedUserIdx}
              selectOnlyOptionAutomatically
              required
              disabled={selectedOfficeId == null}
              onChange={({ option }: SelectedOption) => {
                if (option.payload?.id == null) {
                  reporter.captureMessage(
                    `${cleanedFilename(__filename)} | Should not occur | user id is null`,
                    'debug',
                  );
                  return;
                }
                setSelectedUserId(option.payload.id);
              }}
            />
          </JustificationContainer>
        </BasePopover>
      )}
    </>
  );
};

export default ReassignOfficeUser;
